import {
  Stack,
  Button,
  Group,
  TextInput,
  Text,
  Dialog,
  Select,
  Loader,
} from "@mantine/core"
import { useTranslation } from "react-i18next"
import { UseMutationResult } from "@tanstack/react-query"
import {
  EditResourceDetailsFormData,
  ResourceObjectMonetaryValue,
  UpdateValuationData,
} from "../types"
import { DefaultModal } from "@components/default-modal"
import { PriceInput } from "../../PriceInput"
import { useGetConditionsByResourceObjectId } from "@/orval/gen"

interface EditResourceDetailsModalProps {
  opened: boolean
  onClose: () => void
  resourceObjectId?: number
  editFormData: EditResourceDetailsFormData
  setEditFormData: (data: EditResourceDetailsFormData) => void
  updateResourceObjectMutation: UseMutationResult<
    any,
    Error,
    EditResourceDetailsFormData
  >
  createValuationMutation?: UseMutationResult<any, Error, number>
  updateValuationMutation?: UseMutationResult<any, Error, UpdateValuationData>
  monetaryValues?: ResourceObjectMonetaryValue[]
}

export function EditResourceDetailsModal({
  opened,
  onClose,
  resourceObjectId,
  editFormData,
  setEditFormData,
  updateResourceObjectMutation,
  createValuationMutation,
  updateValuationMutation,
  monetaryValues = [],
}: EditResourceDetailsModalProps) {
  const { t } = useTranslation("app_server")
  const [valuationInput, setValuationInput] = React.useState("")
  const [isSaving, setIsSaving] = React.useState(false)
  const [showReplaceDialog, setShowReplaceDialog] = React.useState(false)
  const [existingValuationForReplacement, setExistingValuationForReplacement] =
    React.useState<ResourceObjectMonetaryValue | null>(null)

  const { data: conditions = [], isLoading: isLoadingConditions } =
    useGetConditionsByResourceObjectId(
      { resourceObjectId: resourceObjectId! },
      {
        query: {
          enabled: opened && !!resourceObjectId,
        },
      }
    )

  const handleSaveValuation = async () => {
    if (!editFormData.valuation) return

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const existingValuation = monetaryValues.find((m) => {
      if (!m.estimateDate) return false
      const existingDate = new Date(m.estimateDate)
      existingDate.setHours(0, 0, 0, 0)
      return existingDate.getTime() === today.getTime()
    })

    if (existingValuation) {
      setExistingValuationForReplacement(existingValuation)
      setShowReplaceDialog(true)
    } else {
      await createValuationMutation?.mutateAsync(editFormData.valuation)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await updateResourceObjectMutation.mutateAsync(editFormData)
      if (editFormData.valuation) {
        await handleSaveValuation()
      }
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  const handleReplaceValuation = async () => {
    if (
      !editFormData.valuation ||
      !existingValuationForReplacement?.resourceObjectMonetaryValueId
    )
      return

    setShowReplaceDialog(false)
    await updateValuationMutation?.mutateAsync({
      resourceObjectMonetaryValueId:
        existingValuationForReplacement.resourceObjectMonetaryValueId,
      monetaryValue: editFormData.valuation,
    })
  }

  React.useEffect(() => {
    if (opened) {
      setValuationInput("")
    }
  }, [opened])

  return (
    <>
      <DefaultModal
        opened={opened}
        onClose={() => {
          onClose()
          updateResourceObjectMutation.reset()
        }}
        title={t("Edit")}
      >
        {opened && (
          <Stack gap="md">
            <TextInput
              label={t("Name")}
              value={editFormData.name}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  name: e.currentTarget.value,
                })
              }
              required
            />

            <TextInput
              label={t("InventoryNumber")}
              value={editFormData.identificationCode}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  identificationCode: e.currentTarget.value,
                })
              }
            />

            <Select
              label={t("Condition")}
              placeholder={t("SelectCondition")}
              value={
                editFormData.conditionId
                  ? editFormData.conditionId.toString()
                  : null
              }
              onChange={(value) =>
                setEditFormData({
                  ...editFormData,
                  conditionId: value ? parseInt(value, 10) : null,
                })
              }
              data={conditions.map((condition) => ({
                value: condition.resourceObjectConditionId.toString(),
                label:
                  condition.name ??
                  condition.resourceObjectConditionId.toString(),
              }))}
              rightSection={
                isLoadingConditions ? <Loader size="xs" /> : undefined
              }
              disabled={isLoadingConditions}
              searchable
              clearable
            />

            <PriceInput
              label={t("PurchasePrice")}
              value={valuationInput}
              onChange={(cleanedInput, numValue) => {
                setValuationInput(cleanedInput)
                setEditFormData({
                  ...editFormData,
                  valuation: numValue ?? null,
                })
              }}
            />

            {updateResourceObjectMutation.error && (
              <Text color="red" size="sm">
                {(updateResourceObjectMutation.error as Error).message}
              </Text>
            )}

            <Group justify="flex-end" gap="md">
              <Button variant="default" onClick={onClose}>
                {t("Cancel")}
              </Button>
              <Button onClick={handleSave} loading={isSaving}>
                {t("Save")}
              </Button>
            </Group>
          </Stack>
        )}
      </DefaultModal>

      <Dialog
        opened={showReplaceDialog}
        onClose={() => setShowReplaceDialog(false)}
      >
        <Text size="sm" mb="md">
          {t("ValuationAtThisDateExistsDescription")}
        </Text>
        <Group justify="flex-end" gap="md">
          <Button variant="default" onClick={() => setShowReplaceDialog(false)}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleReplaceValuation} loading={isSaving}>
            {t("Replace")}
          </Button>
        </Group>
      </Dialog>
    </>
  )
}
